import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, NavLink } from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import MentionAnalysis from './pages/MentionAnalysis';
import NewsList from './pages/NewsList';
import UploadExcel from './pages/UploadExcel';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import axios from 'axios';

axios.defaults.headers.common['Content-Type'] = 'application/json';

function AppContent() {
    const [user, setUser] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        if (storedUser) {
          setUser(JSON.parse(storedUser));
        }
      }
    }, []);

  const login = (userData, token) => {
    setUser(userData);
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData)); // Store full user incl role
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    navigate('/dashboard');
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Routes>
        <Route path="/login" element={!user ? <Login onLogin={login} /> : <Navigate to="/dashboard" />} />
        <Route path="/" element={<Navigate to="/dashboard" />} />
        <Route path="/dashboard" element={user ? <Layout user={user} logout={logout}><Dashboard /></Layout> : <Navigate to="/login" />} />
        <Route path="/news" element={user ? <Layout user={user} logout={logout}><NewsList /></Layout> : <Navigate to="/login" />} />
        <Route path="/mention-analysis" element={user ? <Layout user={user} logout={logout}><MentionAnalysis /></Layout> : <Navigate to="/login" />} />
        <Route path="/upload" element={user ? <Layout user={user} logout={logout}><UploadExcel /></Layout> : <Navigate to="/login" />} />
        
        <Route path="/reports" element={user ? <Layout user={user} logout={logout}><Reports /></Layout> : <Navigate to="/login" />} />
        <Route path="/settings" element={user ? <Layout user={user} logout={logout}><Settings user={user} logout={logout} /></Layout> : <Navigate to="/login" />} />
      </Routes>
    </div>
  );
}

function Layout({ user, logout, children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const navLinks = [
    { name: 'Dashboard', path: '/dashboard', icon: '📊' },
    { name: 'News', path: '/news', icon: '📰' },
    { name: 'Mention Analysis', path: '/mention-analysis', icon: '🎯' },
    { name: 'Upload Excel', path: '/upload', icon: '📁' },
    { name: 'Reports', path: '/reports', icon: '📈' },
    { name: 'Settings', path: '/settings', icon: '⚙️' },
  ];

  return (
    <div className="flex w-full min-h-screen overflow-hidden" id="3cxfdj">

      <div className={`fixed inset-0 z-40 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="absolute inset-0 bg-black/40" onClick={() => setSidebarOpen(false)} />
        <aside className="absolute inset-y-0 left-0 w-72 bg-white shadow-xl p-6 overflow-y-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Media Monitor</h1>
              <p className="text-sm text-gray-500">Sistem Monitoring Media</p>
            </div>
            <button
              className="text-gray-500 hover:text-gray-700"
              onClick={() => setSidebarOpen(false)}
            >
              ✕
            </button>
          </div>
          <nav className="space-y-2">
            {navLinks.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `block rounded-2xl px-4 py-3 text-sm font-medium transition ${
                    isActive ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700 hover:bg-gray-100'
                  }`
                }
              >
                <span className="mr-2">{link.icon}</span>
                {link.name}
              </NavLink>
            ))}
          </nav>
          <div className="mt-10 pt-6 border-t border-gray-200">
            <div className="text-sm text-gray-600">Logged in as</div>
            <div className="mt-1 font-medium text-gray-900 truncate">{user.email}</div>
            <button
              onClick={logout}
              className="mt-4 w-full bg-red-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-red-700"
            >
              Logout
            </button>
          </div>
        </aside>
      </div>

      <aside className={`hidden lg:flex ${collapsed ? 'w-24 xl:w-28' : 'w-64'} flex-col border-r border-gray-200 bg-white transition-all duration-300`}>
        <div className="flex items-center justify-between px-5 py-6">
          <div className={`${collapsed ? 'hidden' : ''}`}>
            <h1 className="text-2xl font-bold text-gray-900">Media Monitor</h1>
            <p className="mt-2 text-sm text-gray-500">Sistem Monitoring Media</p>
          </div>
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="rounded-full border border-gray-200 p-2 text-gray-600 hover:bg-gray-100"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? '→' : '←'}
          </button>
        </div>
        <nav className="flex-1 px-2 space-y-2">
          {navLinks.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition ${
                  isActive ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700 hover:bg-gray-100'
                }`}
            >
              <span className="text-lg">{link.icon}</span>
              <span className={`${collapsed ? 'hidden' : 'block'}`}>{link.name}</span>
            </NavLink>
          ))}
        </nav>
        <div className={`px-4 py-6 border-t border-gray-200 ${collapsed ? 'items-center text-center' : ''} flex flex-col gap-3`}>
          <div className="text-sm text-gray-600">Logged in as</div>
          <div className={`font-medium text-gray-900 truncate ${collapsed ? 'w-full' : ''}`}>{user.email}</div>
          <button
            onClick={logout}
            className="mt-2 w-full bg-red-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-red-700"
          >
            Logout
          </button>
        </div>
      </aside>

      <div className="flex-1 overflow-auto min-w-0 w-full" id="5b5nsu">
        <main className="w-full px-4 sm:px-6 lg:px-8 py-6" id="h0d32k">
          <div className="mx-auto w-full max-w-7xl">
            <div className="mb-4 hidden lg:block">
              <div className="relative bg-gradient-to-r from-indigo-600 to-sky-500 px-8 py-6 text-white shadow-none rounded-3xl lg:rounded-[2rem]">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h2 className="text-2xl font-semibold">Selamat datang kembali</h2>
                    <p className="mt-1 text-sm text-white/80">Silakan pilih menu di panel samping untuk memulai.</p>
                  </div>
                  <div className="rounded-3xl bg-white/10 px-4 py-3 text-sm">
                    {user.email}
                  </div>
                </div>
              </div>
            </div>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

